/**
 * Guardrails settings — approval policy, risk caps, playbook view, autonomy dial.
 * Safety-critical, password-gated where applicable.
 *
 * v0.5.4 AE-068: sub-tab is URL state (`?sub=playbook|autonomy|...`) so
 * FocalCard deeplinks (`/settings?tab=guardrails&sub=playbook`) land here
 * with the right sub-tab active.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as api from '../../lib/api';
import { useToast } from '../../lib/toast';
import { PlaybookPane } from '../PlaybookPane';
import { AutonomyDialPane } from '../AutonomyDialPane';

const SUBS = ['approval', 'risk', 'playbook', 'autonomy'] as const;
type Sub = typeof SUBS[number];

export function GuardrailsSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawSub = (searchParams.get('sub') ?? 'approval').toLowerCase();
  const sub: Sub = (SUBS as readonly string[]).includes(rawSub) ? (rawSub as Sub) : 'approval';

  function setSub(next: Sub) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'approval') {
      sp.delete('sub');
    } else {
      sp.set('sub', next);
    }
    setSearchParams(sp, { replace: true });
  }

  return (
    <div>
      <div className="flex gap-1 mb-4">
        {SUBS.map((t) => (
          <button
            key={t}
            onClick={() => setSub(t)}
            data-testid={`guardrails-sub-${t}`}
            className={`text-xs px-2 py-1 rounded transition ${
              sub === t ? 'bg-bg-elev border border-edge text-ink' : 'text-ink-mute hover:text-ink'
            }`}
          >
            {t === 'approval' ? 'Approval policy' : t === 'risk' ? 'Risk caps' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>
      {sub === 'approval' && <ApprovalSection />}
      {sub === 'risk' && <RiskSection />}
      {sub === 'playbook' && <PlaybookPane />}
      {sub === 'autonomy' && <AutonomyDialPane />}
    </div>
  );
}

function ApprovalSection() {
  const toast = useToast();
  const [pol, setPol] = useState<api.ApprovalPolicy | null>(null);
  const [autoSim, setAutoSim] = useState(true);
  const [perTrade, setPerTrade] = useState<string>('');
  const [daily, setDaily] = useState<string>('');
  const [weekly, setWeekly] = useState<string>('');
  const [monthly, setMonthly] = useState<string>('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.settings.getApproval().then((r) => {
      setPol(r);
      setAutoSim(r.auto_approve_sim);
      setPerTrade(r.auto_approve_live_under_usd != null ? String(r.auto_approve_live_under_usd) : '');
      setDaily(r.daily_live_usd_limit != null ? String(r.daily_live_usd_limit) : '');
      setWeekly(r.weekly_live_usd_limit != null ? String(r.weekly_live_usd_limit) : '');
      setMonthly(r.monthly_live_usd_limit != null ? String(r.monthly_live_usd_limit) : '');
    }).catch((e) => toast.show(e.message, 'bad'));
  }, []);

  function diff(): api.ApprovalPolicyUpdate {
    if (!pol) return {};
    const out: api.ApprovalPolicyUpdate = {};
    const clear: string[] = [];
    if (autoSim !== pol.auto_approve_sim) out.auto_approve_sim = autoSim;
    const ptN = perTrade === '' ? null : Number(perTrade);
    if (ptN !== pol.auto_approve_live_under_usd) { if (ptN === null) clear.push('auto_approve_live_under_usd'); else out.auto_approve_live_under_usd = ptN; }
    const dN = daily === '' ? null : Number(daily);
    if (dN !== pol.daily_live_usd_limit) { if (dN === null) clear.push('daily_live_usd_limit'); else out.daily_live_usd_limit = dN; }
    const wN = weekly === '' ? null : Number(weekly);
    if (wN !== pol.weekly_live_usd_limit) { if (wN === null) clear.push('weekly_live_usd_limit'); else out.weekly_live_usd_limit = wN; }
    const mN = monthly === '' ? null : Number(monthly);
    if (mN !== pol.monthly_live_usd_limit) { if (mN === null) clear.push('monthly_live_usd_limit'); else out.monthly_live_usd_limit = mN; }
    if (clear.length) out.clear = clear;
    return out;
  }

  const changes = diff();
  const liveChange =
    changes.auto_approve_live_under_usd !== undefined ||
    changes.daily_live_usd_limit !== undefined ||
    changes.weekly_live_usd_limit !== undefined ||
    changes.monthly_live_usd_limit !== undefined ||
    (changes.clear ?? []).some((c) => c.startsWith('auto_approve_live') || c.startsWith('daily') || c.startsWith('weekly') || c.startsWith('monthly'));

  async function save() {
    setBusy(true);
    try {
      const body = { ...changes };
      if (liveChange) body.password = pw;
      const r = await api.settings.setApproval(body);
      setPol(r);
      setPw('');
      toast.show('Approval policy saved', 'ok');
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Approval policy (Layer 11)" subtitle="Every order pauses for approval. These knobs decide which auto-approve and which need your click.">
      {pol && (
        <div className="text-[11px] text-ink-mute mb-3 grid grid-cols-3 gap-2">
          <Stat label="LIVE today" value={`$${pol.aggregates.daily.toFixed(2)}`} />
          <Stat label="LIVE this week" value={`$${pol.aggregates.weekly.toFixed(2)}`} />
          <Stat label="LIVE this month" value={`$${pol.aggregates.monthly.toFixed(2)}`} />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <label className="block col-span-full">
          <input type="checkbox" className="mr-2" checked={autoSim} onChange={(e) => setAutoSim(e.target.checked)} />
          auto-approve SIM (default ON; SIM is virtual money)
        </label>
        <label className="block col-span-full">
          <span className="text-ink-mute">auto-approve LIVE orders under $ (blank = always require click)</span>
          <input className="input w-full mt-1 num" type="number" min={0} step="any" value={perTrade} onChange={(e) => setPerTrade(e.target.value)} placeholder="e.g. 1000" />
        </label>
        <label className="block">
          <span className="text-ink-mute">daily LIVE $ limit</span>
          <input className="input w-full mt-1 num" type="number" min={0} value={daily} onChange={(e) => setDaily(e.target.value)} placeholder="optional" />
        </label>
        <label className="block">
          <span className="text-ink-mute">weekly LIVE $ limit</span>
          <input className="input w-full mt-1 num" type="number" min={0} value={weekly} onChange={(e) => setWeekly(e.target.value)} placeholder="optional" />
        </label>
        <label className="block col-span-full">
          <span className="text-ink-mute">monthly LIVE $ limit</span>
          <input className="input w-full mt-1 num" type="number" min={0} value={monthly} onChange={(e) => setMonthly(e.target.value)} placeholder="optional" />
        </label>
        {liveChange && (
          <label className="block col-span-full">
            <span className="text-ink-mute">password (required to change LIVE thresholds)</span>
            <input type="password" className="input w-full mt-1" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="your password" />
          </label>
        )}
        <div className="col-span-full flex items-center gap-2">
          <button className="btn" onClick={save} disabled={busy || Object.keys(changes).length === 0 || (liveChange && !pw)}>save</button>
        </div>
      </div>
    </Section>
  );
}

function RiskSection() {
  const toast = useToast();
  const [maxTrade, setMaxTrade] = useState<string>('');
  const [maxDD, setMaxDD] = useState<string>('');
  const [allowlist, setAllowlist] = useState<string>('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.settings.getRisk().then((r) => {
      setMaxTrade(r.max_trade_usd != null ? String(r.max_trade_usd) : '');
      setMaxDD(r.max_daily_drawdown_pct != null ? String(r.max_daily_drawdown_pct) : '');
      setAllowlist(r.instrument_allowlist ?? '');
    }).catch((e) => toast.show(e.message, 'bad'));
  }, []);

  async function save() {
    setBusy(true);
    try {
      await api.settings.setRisk({
        max_trade_usd: maxTrade ? Number(maxTrade) : null,
        max_daily_drawdown_pct: maxDD ? Number(maxDD) : null,
        instrument_allowlist: allowlist || null,
      });
      toast.show('Risk caps saved', 'ok');
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Risk caps" subtitle="Enforced on every order BEFORE Compliance. Per-user.">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <label className="block">
          <span className="text-ink-mute">max per-trade USD</span>
          <input className="input w-full mt-1 num" type="number" value={maxTrade} onChange={(e) => setMaxTrade(e.target.value)} placeholder="e.g. 5000" />
        </label>
        <label className="block">
          <span className="text-ink-mute">max daily drawdown %</span>
          <input className="input w-full mt-1 num" type="number" step="0.5" value={maxDD} onChange={(e) => setMaxDD(e.target.value)} placeholder="e.g. 2" />
        </label>
        <label className="block col-span-full">
          <span className="text-ink-mute">instrument allowlist (regex per line; empty = allow all)</span>
          <textarea className="input w-full mt-1 font-mono" rows={3} value={allowlist} onChange={(e) => setAllowlist(e.target.value)} placeholder={"^[A-Z]{1,5}$\nSPY"} />
        </label>
        <div className="col-span-full">
          <button className="btn" onClick={save} disabled={busy}>save</button>
        </div>
      </div>
    </Section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-2 text-center">
      <div className="text-[10px] text-ink-mute uppercase">{label}</div>
      <div className="num text-sm">{value}</div>
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <div className="text-sm font-semibold mb-1">{title}</div>
      {subtitle && <div className="text-[11px] text-ink-mute mb-3">{subtitle}</div>}
      {children}
    </div>
  );
}
